import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/apiClient";
import { useCart } from "../store/cart";
import { toast } from "sonner";
import { ChevronRight, Truck, Shield, Flame, ArrowRight } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setSize(r.data.sizes?.length === 1 ? r.data.sizes[0] : null);
        setImgIdx(0);
        setQty(1);
      })
      .finally(() => setLoading(false));
    api.get("/products").then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (loading) return <div className="font-mono text-ash text-center py-32" data-testid="pdp-loading">+ + + LOADING + + +</div>;
  if (!product) return <div className="font-marker text-blood text-center py-32 text-3xl">product not found.</div>;

  return (
    <div data-testid="product-detail-page" className="bg-ink">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <nav className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-ash font-mono">
          <Link to="/" className="hover:text-blood">HOME</Link>
          <ChevronRight size={12} />
          <Link to="/shop" className="hover:text-blood">SHOP</Link>
          <ChevronRight size={12} />
          <span className="text-blood truncate">{product.name}</span>
        </nav>
      </div>

      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 px-4 sm:px-6 lg:px-8 py-10">
        {/* Image gallery */}
        <div className="lg:col-span-7 lg:sticky lg:top-32 self-start">
          <div className="relative aspect-square bg-surface border border-white/10 overflow-hidden group">
            <img
              data-testid="product-main-image"
              src={product.images?.[imgIdx]}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute top-4 left-4 bg-blood text-bone px-3 py-1 text-[10px] tracking-[0.3em] font-mono">
              FRAME / {String(imgIdx + 1).padStart(3, "0")}
            </span>
            {product.featured && (
              <span className="absolute top-4 right-4 border border-bone text-bone px-3 py-1 text-[10px] tracking-[0.3em] font-mono bg-ink/60 backdrop-blur">
                FEATURED
              </span>
            )}
            <span className="absolute bottom-4 right-4 font-marker text-bone text-3xl rotate-[-3deg]">live.</span>
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 mt-3">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`w-20 h-20 border ${i === imgIdx ? "border-blood" : "border-white/15"} hover:border-blood transition-colors`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <p className="text-blood text-xs tracking-[0.4em] font-mono">// {product.category.toUpperCase()} / DROP 001</p>
          <h1 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter mt-2 leading-[0.85]">
            {product.name}
          </h1>
          {product.tagline && (
            <p className="font-marker text-blood text-2xl mt-3">{product.tagline.toLowerCase()}</p>
          )}

          <div className="flex items-baseline gap-4 mt-6 border-t border-b border-white/10 py-4">
            <span className="font-anton text-bone text-4xl">${product.price.toFixed(2)}</span>
            <span className="text-[10px] tracking-[0.3em] font-mono text-ash uppercase">USD / TAX INCL.</span>
          </div>

          {/* Size */}
          <div className="mt-8">
            <div className="flex justify-between items-baseline mb-3">
              <p className="text-xs tracking-[0.3em] uppercase text-bone font-mono">// SELECT SIZE</p>
              <button type="button" className="text-[10px] tracking-[0.3em] uppercase text-ash hover:text-blood font-mono">
                SIZE GUIDE
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((s) => (
                <button
                  key={s}
                  data-testid={`size-selector-${s}`}
                  onClick={() => setSize(s)}
                  className={`min-w-[56px] px-4 py-3 border text-sm font-mono transition-colors ${
                    size === s
                      ? "bg-bone text-ink border-bone"
                      : "border-white/20 text-bone hover:border-blood"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Qty */}
          <div className="mt-6 flex items-center gap-4">
            <p className="text-xs tracking-[0.3em] uppercase text-bone font-mono">// QTY</p>
            <div className="inline-flex border border-white/20">
              <button data-testid="qty-decrement" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 text-bone hover:bg-blood">−</button>
              <span className="w-12 h-10 leading-10 text-center font-mono text-bone">{qty}</span>
              <button data-testid="qty-increment" onClick={() => setQty(qty + 1)} className="w-10 h-10 text-bone hover:bg-blood">+</button>
            </div>
          </div>

          <button
            data-testid="add-to-cart-button"
            onClick={() => {
              if (!size) { toast.error("PICK A SIZE, REBEL."); return; }
              addItem(product, size, qty);
              toast.success(`${product.name} → BAG. STAY DANGEROUS.`);
            }}
            className="mt-8 w-full px-7 py-5 bg-blood text-bone text-sm tracking-[0.4em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors flex items-center justify-center gap-2 group"
          >
            ADD TO BAG
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Trust strip */}
          <div className="mt-6 grid grid-cols-3 gap-2 text-center border border-white/10">
            <div className="p-3 border-r border-white/10">
              <Truck size={16} className="text-blood mx-auto" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-ash mt-2 font-mono">FREE SHIP $100+</p>
            </div>
            <div className="p-3 border-r border-white/10">
              <Shield size={16} className="text-blood mx-auto" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-ash mt-2 font-mono">SECURE CHECKOUT</p>
            </div>
            <div className="p-3">
              <Flame size={16} className="text-blood mx-auto" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-ash mt-2 font-mono">LIMITED RUN</p>
            </div>
          </div>

          {/* Details */}
          <div className="mt-10 pt-6 border-t border-white/15 space-y-4">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-blood font-mono mb-2">// THE BUILD</p>
              <p className="text-bone text-sm leading-relaxed">{product.description}</p>
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-blood font-mono mb-2">// STATUS</p>
              <p className="text-bone text-sm font-mono">
                {product.stock > 0 ? <span><span className="text-blood">●</span> LIVE / {product.stock} REMAINING</span> : "SOLD OUT"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <section className="border-t border-white/15">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
            <h2 className="font-anton uppercase text-bone text-4xl sm:text-5xl tracking-tighter mb-8">
              MORE <span className="text-blood">HEAT</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group block border border-white/10 hard-shadow"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-surface">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                  </div>
                  <div className="p-4 border-t border-white/10 flex items-baseline justify-between gap-3">
                    <h3 className="font-anton uppercase text-bone text-lg tracking-tighter truncate group-hover:text-blood transition-colors">{p.name}</h3>
                    <span className="font-mono text-bone text-sm">${p.price.toFixed(2)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
